import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Mail, FileText, History, Eye, Send, Save, Power,
  ChevronDown, ChevronUp, Clock, CheckCircle2, XCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_LABELS = {
  confirmation: { name: 'Confirmacion de reserva', icon: '1', desc: 'Se envia al completar la compra/reserva' },
  reminder: { name: 'Recordatorio 24h', icon: '2', desc: 'Se envia 24 horas antes del tour' },
  thankyou: { name: 'Agradecimiento post-tour', icon: '3', desc: 'Se envia tras finalizar el tour' },
};

const VARIABLES = [
  { key: 'customer_name', label: 'Nombre cliente' },
  { key: 'tour_name', label: 'Tour' },
  { key: 'tour_date', label: 'Fecha' },
  { key: 'tour_time', label: 'Hora' },
  { key: 'passenger_count', label: 'Pasajeros' },
  { key: 'adults', label: 'Adultos' },
  { key: 'children', label: 'Ninos' },
  { key: 'price', label: 'Precio' },
  { key: 'meeting_point', label: 'Punto encuentro' },
  { key: 'reservation_id', label: 'ID Reserva' },
  { key: 'review_link', label: 'Link resena' },
  { key: 'tip_link', label: 'Link propina' },
  { key: 'contact_email', label: 'Email contacto' },
  { key: 'contact_phone', label: 'Telefono' },
  { key: 'tuk_tuk_name', label: 'Tuk-Tuk' },
];

const STATUS_MAP = {
  sent: { label: 'Enviado', color: 'text-emerald-600 bg-emerald-50', icon: CheckCircle2 },
  failed: { label: 'Fallido', color: 'text-red-600 bg-red-50', icon: XCircle },
  test: { label: 'Prueba', color: 'text-amber-600 bg-amber-50', icon: AlertCircle },
};

const TYPE_LABELS = {
  confirmation: 'Confirmacion',
  reminder: 'Recordatorio',
  thankyou: 'Agradecimiento',
  test: 'Prueba',
  manual: 'Manual',
};

function getAuthHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('adminToken')}` };
}

// ─── Variable insertion button bar ───
function VariableBar({ onInsert }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs font-medium text-stone-500 hover:text-[#C2410C] transition-colors"
        data-testid="variable-bar-toggle"
      >
        <span>{open ? 'Ocultar' : 'Insertar'} variables</span>
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {open && (
        <div className="flex flex-wrap gap-1.5 mt-2 p-3 bg-stone-50 rounded-lg border border-stone-200">
          {VARIABLES.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => onInsert(`{{${v.key}}}`)}
              className="px-2 py-1 text-xs bg-white border border-stone-200 rounded-md hover:border-[#C2410C] hover:text-[#C2410C] transition-colors font-mono"
              data-testid={`var-btn-${v.key}`}
            >
              {v.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Template Editor ───
function TemplateEditor({ template, onSave, onCancel }) {
  const [form, setForm] = useState({ ...template });
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const set = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const insertVariable = useCallback((field, variable) => {
    const el = document.getElementById(`field-${field}`);
    if (el) {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const text = form[field] || '';
      const newText = text.substring(0, start) + variable + text.substring(end);
      set(field, newText);
      setTimeout(() => {
        el.focus();
        el.selectionStart = el.selectionEnd = start + variable.length;
      }, 0);
    } else {
      set(field, (form[field] || '') + variable);
    }
  }, [form]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { id, name, ...data } = form;
      await axios.put(`${API}/admin/emails/templates/${template.id}`, data, { headers: getAuthHeaders() });
      toast.success('Plantilla guardada');
      onSave();
    } catch (err) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const loadPreview = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/emails/templates/${template.id}/preview`, { headers: getAuthHeaders() });
      setPreviewSubject(data.subject);
      setPreviewHtml(data.html);
      setShowPreview(true);
    } catch {
      toast.error('Error al cargar vista previa');
    }
  };

  const handleTestSend = async () => {
    if (!testEmail) { toast.error('Introduce un email'); return; }
    setSendingTest(true);
    try {
      await axios.post(`${API}/admin/emails/templates/test-send`, { to: testEmail, template_id: template.id }, { headers: getAuthHeaders() });
      toast.success(`Prueba enviada a ${testEmail}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar prueba');
    } finally {
      setSendingTest(false);
    }
  };

  const fieldConfig = [
    { key: 'subject', label: 'Asunto', type: 'input', placeholder: 'Asunto del email...' },
    { key: 'main_text', label: 'Texto principal', type: 'textarea', rows: 8, placeholder: 'Contenido principal del email...' },
    { key: 'secondary_text', label: 'Texto secundario', type: 'textarea', rows: 3, placeholder: 'Texto adicional...' },
    { key: 'signature', label: 'Firma', type: 'textarea', rows: 3, placeholder: 'Nombre y datos de contacto...' },
    { key: 'cta_text', label: 'Texto del boton (CTA)', type: 'input', placeholder: 'Ej: Deja tu valoracion' },
    { key: 'cta_url', label: 'URL del boton', type: 'input', placeholder: 'https://...' },
  ];

  return (
    <div className="space-y-6" data-testid={`template-editor-${template.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-stone-900">{TEMPLATE_LABELS[template.id]?.name || template.name}</h3>
          <p className="text-sm text-stone-500 mt-0.5">{TEMPLATE_LABELS[template.id]?.desc}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-stone-600">{form.active ? 'Activo' : 'Inactivo'}</span>
            <Switch
              checked={form.active}
              onCheckedChange={(val) => set('active', val)}
              data-testid={`template-active-toggle-${template.id}`}
            />
          </div>
        </div>
      </div>

      {/* Fields */}
      {fieldConfig.map(({ key, label, type, rows, placeholder }) => (
        <div key={key}>
          <label className="block text-sm font-medium text-stone-700 mb-1.5">{label}</label>
          {['main_text', 'secondary_text', 'signature'].includes(key) && (
            <VariableBar onInsert={(v) => insertVariable(key, v)} />
          )}
          {key === 'subject' && (
            <VariableBar onInsert={(v) => insertVariable(key, v)} />
          )}
          {type === 'input' ? (
            <input
              id={`field-${key}`}
              type="text"
              value={form[key] || ''}
              onChange={(e) => set(key, e.target.value)}
              placeholder={placeholder}
              className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C2410C]/20 focus:border-[#C2410C] bg-white"
              data-testid={`template-field-${key}`}
            />
          ) : (
            <textarea
              id={`field-${key}`}
              value={form[key] || ''}
              onChange={(e) => set(key, e.target.value)}
              rows={rows}
              placeholder={placeholder}
              className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C2410C]/20 focus:border-[#C2410C] bg-white font-mono resize-y"
              data-testid={`template-field-${key}`}
            />
          )}
        </div>
      ))}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-stone-200">
        <Button onClick={handleSave} disabled={saving} className="bg-[#C2410C] hover:bg-[#9A3412] text-white" data-testid="template-save-btn">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Guardando...' : 'Guardar plantilla'}
        </Button>
        <Button variant="outline" onClick={loadPreview} data-testid="template-preview-btn">
          <Eye className="h-4 w-4 mr-2" />Vista previa
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="Email de prueba..."
            className="px-3 py-2 text-sm border border-stone-200 rounded-lg w-56 focus:outline-none focus:ring-2 focus:ring-[#C2410C]/20 focus:border-[#C2410C]"
            data-testid="test-email-input"
          />
          <Button variant="outline" onClick={handleTestSend} disabled={sendingTest} data-testid="test-send-btn">
            <Send className="h-4 w-4 mr-2" />{sendingTest ? 'Enviando...' : 'Enviar prueba'}
          </Button>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-stone-200 flex items-center justify-between">
              <div>
                <p className="text-xs text-stone-400 uppercase tracking-wider">Vista previa</p>
                <p className="text-sm font-medium text-stone-800 mt-1">{previewSubject}</p>
              </div>
              <button onClick={() => setShowPreview(false)} className="text-stone-400 hover:text-stone-600 text-xl font-bold">&times;</button>
            </div>
            <div className="flex-1 overflow-auto p-1">
              <iframe
                title="Email Preview"
                srcDoc={previewHtml}
                className="w-full h-[500px] border-0"
                sandbox=""
                data-testid="email-preview-iframe"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── History Table ───
function EmailHistory() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/emails/log`, { headers: getAuthHeaders() });
        setLogs(data);
      } catch {
        toast.error('Error al cargar historial');
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, []);

  if (loading) return <div className="text-center py-12 text-stone-400">Cargando historial...</div>;
  if (logs.length === 0) return <div className="text-center py-12 text-stone-400">No hay emails enviados todavia</div>;

  return (
    <div className="overflow-x-auto" data-testid="email-history-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-stone-200">
            <th className="text-left py-3 px-4 font-medium text-stone-500">Tipo</th>
            <th className="text-left py-3 px-4 font-medium text-stone-500">Destinatario</th>
            <th className="text-left py-3 px-4 font-medium text-stone-500">Asunto</th>
            <th className="text-left py-3 px-4 font-medium text-stone-500">Reserva</th>
            <th className="text-left py-3 px-4 font-medium text-stone-500">Fecha</th>
            <th className="text-left py-3 px-4 font-medium text-stone-500">Estado</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log, i) => {
            const statusInfo = STATUS_MAP[log.status] || STATUS_MAP.failed;
            const StatusIcon = statusInfo.icon;
            return (
              <tr key={log.id || i} className="border-b border-stone-100 hover:bg-stone-50/50">
                <td className="py-3 px-4">
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-600">
                    {TYPE_LABELS[log.type] || log.type}
                  </span>
                </td>
                <td className="py-3 px-4 text-stone-700 font-mono text-xs">{log.to}</td>
                <td className="py-3 px-4 text-stone-700 max-w-xs truncate">{log.subject}</td>
                <td className="py-3 px-4 text-stone-500 font-mono text-xs">{log.booking_id ? log.booking_id.slice(0, 8) + '...' : '-'}</td>
                <td className="py-3 px-4 text-stone-500 text-xs">
                  {log.created_at ? new Date(log.created_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                </td>
                <td className="py-3 px-4">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                    <StatusIcon className="h-3 w-3" />{statusInfo.label}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Page ───
export default function AdminEmail() {
  const [tab, setTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [runningScheduler, setRunningScheduler] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/emails/templates`, { headers: getAuthHeaders() });
      setTemplates(data);
    } catch {
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const runScheduler = async () => {
    setRunningScheduler(true);
    try {
      const { data } = await axios.post(`${API}/admin/emails/check-scheduled`, {}, { headers: getAuthHeaders() });
      toast.success(data.message);
    } catch {
      toast.error('Error al ejecutar envios programados');
    } finally {
      setRunningScheduler(false);
    }
  };

  const tabs = [
    { id: 'templates', label: 'Plantillas', icon: FileText },
    { id: 'history', label: 'Historial', icon: History },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-5xl" data-testid="admin-email-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Emails automaticos</h1>
          <p className="text-sm text-stone-500 mt-1">Configura y edita las plantillas de correos automaticos</p>
        </div>
        <Button
          variant="outline"
          onClick={runScheduler}
          disabled={runningScheduler}
          className="shrink-0"
          data-testid="run-scheduler-btn"
        >
          <Clock className="h-4 w-4 mr-2" />
          {runningScheduler ? 'Ejecutando...' : 'Ejecutar envios programados'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-stone-100 p-1 rounded-lg w-fit">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => { setTab(id); setEditingId(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              tab === id ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
            }`}
            data-testid={`tab-${id}`}
          >
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      {/* Content */}
      {tab === 'templates' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-stone-400">Cargando plantillas...</div>
          ) : editingId ? (
            <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm">
              <TemplateEditor
                template={templates.find((t) => t.id === editingId)}
                onSave={() => { setEditingId(null); fetchTemplates(); }}
                onCancel={() => setEditingId(null)}
              />
            </div>
          ) : (
            templates.map((tmpl) => {
              const info = TEMPLATE_LABELS[tmpl.id] || {};
              return (
                <div
                  key={tmpl.id}
                  className="bg-white rounded-xl border border-stone-200 p-5 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setEditingId(tmpl.id)}
                  data-testid={`template-card-${tmpl.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
                      tmpl.active ? 'bg-[#C2410C]/10 text-[#C2410C]' : 'bg-stone-100 text-stone-400'
                    }`}>
                      {info.icon || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-stone-900">{info.name || tmpl.name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          tmpl.active ? 'bg-emerald-50 text-emerald-600' : 'bg-stone-100 text-stone-400'
                        }`}>
                          {tmpl.active ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      <p className="text-sm text-stone-500 mt-0.5">{info.desc}</p>
                      <p className="text-xs text-stone-400 mt-1 font-mono truncate">{tmpl.subject}</p>
                    </div>
                    <Mail className="h-5 w-5 text-stone-300 flex-shrink-0" />
                  </div>
                </div>
              );
            })
          )}
          {editingId && (
            <button
              onClick={() => setEditingId(null)}
              className="text-sm text-stone-500 hover:text-[#C2410C] transition-colors"
              data-testid="back-to-list-btn"
            >
              &larr; Volver a la lista
            </button>
          )}
        </div>
      )}

      {tab === 'history' && <EmailHistory />}
    </div>
  );
}
